import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    ListItem,
    ListItemText,
    MenuItem,
    Popover,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { useLingui } from "@lingui/react";
import { Download, Lock, MoreVert, TravelExplore } from "@mui/icons-material";
import { msg } from "@lingui/macro";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import TooltipButton from "../buttons/TooltipButton";
import HomeMadeDialog from "../dialogs/HomeMadeDialog";
import FullPageLoader from "../loaders/FullPageLoader";
import { useState } from "react";
import { httpReqBlob } from "../../services/httpReqBlob";
import httpReq from "../../services/httpReq";
import dayjs from 'dayjs'
import { useDispatch, useSelector } from "react-redux";
import { goToCoords } from "../../store/reducers/map";
import { showErrorSnackBar } from "../../store/reducers/errors";
import { toggleVideoOpen } from "../../store/reducers/projects";

const ProjectVideoItem = ({ project, video, setReloadVideosList }) => {
    const theme = useTheme();
    const { _ } = useLingui();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [anchorVideoEl, setAnchorVideoEl] = useState(null);
    const [snackbarKmzVis, setSnackbarKmzVis] = useState(false);
    const [deleteVideoModal, setDeleteVideoModal] = useState(null);

    const localStorageProjects = useSelector((state) => state.projects.openLocalStorage);
    const foundProject = (localStorageProjects && localStorageProjects.length > 0) ? localStorageProjects.find(p => p.id === project.id) : null;
    const localProject = { ...project, ...foundProject };
    const foundVideo = localProject.video ? localProject.video.find(v => v.id === video.id) : null;
    const localVideo = { ...video, ...foundVideo };

    const downLG = useMediaQuery(theme.breakpoints.down('lg'));

    const handleVideoMenuClose = () => {
        setAnchorVideoEl(null);
    };

    const downloadBlob = (blob, fileName) => {
        let a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";
        let url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    const downloadKmz = () => {
        setSnackbarKmzVis(true)
        httpReqBlob('/Video/GenerateKmz/' + localVideo.id, 'GET', null, true).then((blob) => {
            if(!blob) return
            const fileName = localVideo.title + ".kmz"
            downloadBlob(blob, fileName);
            setSnackbarKmzVis(false)
        })
    }

    const deleteSelectedVideo = () => {
        setIsLoading(true)
        httpReq('Video/Delete/' + localVideo.id, 'DELETE', null, true).then((res) => {
            if (!res || res.responseAnyError) return;
            setDeleteVideoModal(null);
            setIsLoading(false)
        })
    }

    const searchOnMap = (project) => {
        if (project.coords) {
            const coords = {
                latitude: project.coords.coordinates[1],
                longitude: project.coords.coordinates[0],
            }
            dispatch(goToCoords(coords));
        } else {
            dispatch(showErrorSnackBar('Coords not found'));
        }
    }

    const handleVideoMenuClick = (e, pIndex, vIndex) => {
        setAnchorVideoEl(e.currentTarget);

    };

    return (
        <ListItem
            key={`video-${localVideo.id}`}
            sx={{
                borderRadius: 1,
                px: 1,
                my: 0.5,
                transition: 'all 0.2s',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            {isLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Caricamento...</Typography>
                </Box>
            ) : (
                <>
                    <ListItemText
                        primary={localVideo.title}
                        primaryTypographyProps={{
                            fontStyle: 'italic',
                            fontSize: 13,
                        }}
                    />
                    {downLG ? (
                        <>
                            <IconButton
                                size="small"
                                onClick={(e) => handleVideoMenuClick(e)}
                            >
                                <MoreVert fontSize="small" />
                            </IconButton>
                            <Popover
                                anchorEl={anchorVideoEl}
                                open={!!anchorVideoEl}
                                onClose={handleVideoMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                }}
                                PaperProps={{
                                    sx: {
                                        p: 1,
                                        borderRadius: 2,
                                        boxShadow: 3,
                                        minWidth: 180
                                    }
                                }}
                            >
                                <MenuItem onClick={() => {
                                    downloadKmz();
                                    handleVideoMenuClose();
                                }}>
                                    <Download fontSize="small"
                                        sx={{ mr: 1 }} /> {_(msg`Scarica KMZ`)}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        dispatch(toggleVideoOpen({ project: project, video: video }));
                                        handleVideoMenuClose();
                                    }}
                                >
                                    {localVideo.isOpen ?
                                        <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> :
                                        <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />} {_(msg`Visualizza`)}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        searchOnMap(video);
                                        handleVideoMenuClose();
                                    }}
                                >
                                    <TravelExplore fontSize="small" sx={{ mr: 1 }} /> {_(msg`Localizza`)}
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setDeleteVideoModal({
                                            dialogNumber: 1,
                                            title: 'Are you sure?',
                                            text: 'This action cannot be undone'
                                        });
                                        handleVideoMenuClose();
                                    }}
                                >
                                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> {_(msg`Elimina`)}
                                </MenuItem>
                            </Popover>
                        </>
                    ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                            {!video.isCompleted ?
                                <>
                                    <TooltipButton title={_(msg`Scarica KMZ`)}
                                        action={() => downloadKmz()}
                                        icon={<Download />} />
                                    <TooltipButton
                                        title={_(msg`Visualizza video`)}
                                        action={() => dispatch(toggleVideoOpen({ project: project, video: video }))}
                                        icon={localVideo.isOpen ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    />
                                    <TooltipButton title={_(msg`Localizza video`)}
                                        icon={<TravelExplore />}
                                        action={() => searchOnMap(video)} />
                                    <TooltipButton title={_(msg`Completa video`)}
                                        icon={<DeleteIcon />} action={() => {
                                        setReloadVideosList(true)
                                            setDeleteVideoModal({
                                                dialogNumber: 1,
                                                title: 'Are you sure?',
                                                text: 'This action cannot be undone'
                                            })
                                        }} />
                                </>
                                :
                                <>
                                    {downLG ? <Lock color="error" size={16} /> :
                                        <TooltipButton type={'error'} color={'red'} action={() => { }} icon={<Lock sx={{ color: '#ffffff' }} />} title={'CLOSED'} />
                                    }
                                </>
                            }
                        </Box>
                    )}

                </>
            )}
            {snackbarKmzVis &&
                <Snackbar
                    open={snackbarKmzVis}
                    sx={{ mt: 10 }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert severity="info" variant="filled" sx={{ width: '100%' }}>
                        {_(msg`Download kmz in corso...`)}
                    </Alert>
                </Snackbar>}
            {/*{snackbarGenerateTrack &&*/}
            {/*    <Snackbar*/}
            {/*        open={snackbarGenerateTrack}*/}
            {/*        sx={{mt: 10}}*/}
            {/*        anchorOrigin={{vertical: 'top', horizontal: 'right'}}*/}
            {/*    >*/}
            {/*        <Alert severity="info" variant="filled" sx={{width: '100%'}}>*/}
            {/*            {_(msg`Caricamento traccia in corso...`)}*/}
            {/*        </Alert>*/}
            {/*    </Snackbar>}*/}
            {deleteVideoModal &&
                <HomeMadeDialog
                    isOpen={deleteVideoModal}
                    title={deleteVideoModal.title}
                    labelConfirm={'Proceed'}
                    labelClose={'Cancel'}
                    fnClose={() => {
                        setDeleteVideoModal(null)
                    }}
                    fnConfirmAction={() => {
                        if (deleteVideoModal.dialogNumber === 1) {
                            setDeleteVideoModal({
                                dialogNumber: 2,
                                title: 'Proceed?',
                                text: 'All associated reports and files will be permanently deleted.'
                            })
                        } else {
                            deleteSelectedVideo()
                        }
                    }}
                    text={deleteVideoModal.text} />}
            {isLoading && <FullPageLoader />}
        </ListItem>
    )
}
export default ProjectVideoItem;